import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import PromoBanner from '../components/PromoBanner';
import { Download, CheckCircle, Lock } from 'lucide-react';

const SolutionsArchitectRoadmapPage = () => {
  const [isUnlocked, setIsUnlocked] = useState(false);

  // Check if user has already unlocked (stored in localStorage)
  useEffect(() => {
    const unlocked = localStorage.getItem('saRoadmapUnlocked');
    if (unlocked === 'true') {
      setIsUnlocked(true);
    }
  }, []);

  // Listen for message from Beehiiv iframe (subscription confirmation)
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && (event.data.type === 'beehiiv:subscribed' || event.data === 'subscribed')) {
        setIsUnlocked(true);
        localStorage.setItem('saRoadmapUnlocked', 'true');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleUnlock = () => {
    setIsUnlocked(true);
    localStorage.setItem('saRoadmapUnlocked', 'true');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PromoBanner />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-sky-50 to-blue-100">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-6xl mb-6">📋</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              90-Day Solutions Architect Checklist
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              A simple checklist to keep you on track during your Solutions Architect journey.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            
            {/* What's Inside */}
            <div className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-xl p-8 mb-8 border border-sky-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                What's Inside?
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Week-by-week checklist</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Key milestones to hit</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Track your progress</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Stay accountable</span>
                </div>
              </div>
            </div>

            {/* Newsletter Subscription / Download Section */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              {!isUnlocked ? (
                <>
                  {/* Locked State - Show Newsletter Form */}
                  <div className="p-8 text-center border-b border-gray-200 bg-gradient-to-r from-sky-50 to-blue-50">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-sky-100 rounded-full mb-4">
                      <Lock className="text-sky-600" size={32} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      Subscribe to Get Your Free Checklist
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Join cloud learners and get the checklist + weekly cloud tips!
                    </p>
                  </div>
                  
                  {/* Beehiiv Embed */}
                  <div className="p-6 bg-white">
                    <div className="flex justify-center">
                      <iframe 
                        src="https://subscribe-forms.beehiiv.com/6144635c-b62f-4fbe-b353-d70ba5621603" 
                        className="beehiiv-embed" 
                        data-test-id="beehiiv-embed" 
                        frameBorder="0" 
                        scrolling="no" 
                        style={{
                          width: '100%',
                          maxWidth: '796px',
                          height: '338px',
                          margin: 0,
                          borderRadius: 0,
                          backgroundColor: 'transparent',
                          boxShadow: 'none'
                        }}
                      />
                    </div>
                    
                    {/* Manual Unlock Button (fallback) */}
                    <div className="text-center mt-6 pt-6 border-t border-gray-200">
                      <p className="text-sm text-gray-500 mb-3">
                        Already subscribed? Click below to access your checklist:
                      </p>
                      <button
                        onClick={handleUnlock}
                        className="text-sky-600 hover:text-sky-700 font-medium text-sm underline"
                      >
                        I've already subscribed → Unlock Checklist
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Unlocked State - Show Download */}
                  <div className="p-8 text-center bg-gradient-to-r from-green-50 to-emerald-50">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                      <CheckCircle className="text-green-600" size={32} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      🎉 Your Checklist is Ready!
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Thanks for subscribing! Download your checklist below.
                    </p>
                    
                    <a
                      href="/90-Day-Solutions-Architect-Roadmap.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-sky-500 to-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transition-all duration-200"
                    >
                      <Download size={24} />
                      Download Checklist
                    </a>
                    
                    <p className="text-sm text-gray-500 mt-4">
                      Check your inbox for weekly cloud tips! 📧
                    </p>
                  </div>
                </>
              )}
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SolutionsArchitectRoadmapPage;
